import type { ChangeEvent, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toCredits } from '../../components/AllocationPanel.js';
import type { CreditImportOutcome, CreditImportRow } from './AllocationsScreen.js';

interface CreditImportPanelProps {
  importText: string;
  importRows: CreditImportRow[];
  importOutcomes: Record<number, CreditImportOutcome>;
  importValidCount: number;
  importInvalidCount: number;
  importAppliedCount: number;
  importFailedCount: number;
  hasUniformImportCredit: boolean;
  importBusy: boolean;
  onImportTextChange: (text: string) => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRetryFailed: () => void;
  onApply: (event: FormEvent) => void;
}

export function CreditImportPanel({
  importText,
  importRows,
  importOutcomes,
  importValidCount,
  importInvalidCount,
  importAppliedCount,
  importFailedCount,
  hasUniformImportCredit,
  importBusy,
  onImportTextChange,
  onFileChange,
  onRetryFailed,
  onApply
}: CreditImportPanelProps) {
  const { t } = useTranslation();
  return (
    <form className="panel credit-import-form" onSubmit={onApply}>
      <div className="credit-import-heading">
        <h2>{t('creditImport.title')}</h2>
        <span className="muted">
          {t('creditImport.summary', {
            total: importRows.length,
            valid: importValidCount,
            invalid: importInvalidCount,
            applied: importAppliedCount
          })}
        </span>
      </div>
      <label>
        <span>{t('creditImport.file')}</span>
        <input type="file" accept=".csv,text/csv" onChange={onFileChange} disabled={importBusy} />
      </label>
      <label className="credit-import-text">
        <span>{t('creditImport.csv')}</span>
        <textarea
          value={importText}
          placeholder={t('creditImport.placeholder')}
          onChange={(event) => onImportTextChange(event.target.value)}
          disabled={importBusy}
          rows={8}
        />
      </label>
      {hasUniformImportCredit && <p className="muted credit-import-hint">{t('creditImport.uniformHint')}</p>}
      <div className="credit-import-actions">
        <button disabled={importBusy || importValidCount === 0 || importInvalidCount > 0}>{t('creditImport.apply')}</button>
        {importFailedCount > 0 && (
          <button type="button" onClick={onRetryFailed} disabled={importBusy}>
            {t('creditImport.retryFailed', { count: importFailedCount })}
          </button>
        )}
        <span className="muted">
          {t('creditImport.progress', {
            applied: importAppliedCount,
            failed: importFailedCount,
            valid: importValidCount
          })}
        </span>
      </div>
      {importRows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('creditImport.line')}</th>
                <th>{t('creditImport.email')}</th>
                <th>{t('creditImport.userId')}</th>
                <th>{t('creditImport.credit')}</th>
                <th>{t('creditImport.status')}</th>
                <th>{t('creditImport.appliedCredit')}</th>
              </tr>
            </thead>
            <tbody>
              {importRows.map((row) => {
                const outcome = importOutcomes[row.line];
                const statusKey = row.errorKey
                  ? 'creditImport.invalid'
                  : outcome
                    ? `creditImport.${outcome.status}`
                    : 'creditImport.ready';
                return (
                  <tr key={row.line}>
                    <td>{row.line}</td>
                    <td>{row.email}</td>
                    <td className="mono">{row.userId ?? t('common.none')}</td>
                    <td>{row.credit}</td>
                    <td className={row.errorKey || outcome?.status === 'failed' ? 'warning' : undefined}>
                      {t(statusKey)}
                      <br />
                      <span className="muted">{row.errorKey ? t(row.errorKey) : (outcome?.message ?? '')}</span>
                    </td>
                    <td>
                      {outcome?.appliedCreditMillis === undefined ? t('common.none') : toCredits(outcome.appliedCreditMillis)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </form>
  );
}
